import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { Reorder } from "framer-motion";
import DraggableItem from "./DraggableItem";
import { PlusOutlined } from "@ant-design/icons";
import MemberDropdown from "./MemberDropdown";
import { SectionRow } from "../components";

const DraggableContainer = styled(Reorder.Group)`
  display: flex;
  overflow-x: auto;
  flex-flow: row nowrap;
  padding: 0 8px;
  max-width: calc(100vw - 130px);
  width: 100%;
  margin-top: 0px !important;
`;

const DraggedMemberGroup = ({
  members: initialMembers,
  availableMembers,
  addMemberName,
  updateMethods,
  intl,
  pivotConfig,
  updatePivotConfig,
}) => {
  const [members, setMembers] = useState(initialMembers);
  const pivotDimensionsRef = useRef([]);

  useEffect(() => {
    const pivotDimensions = pivotConfig?.x?.length ? pivotConfig.x : [];
    pivotDimensionsRef.current = pivotDimensions;
    const orderedMembers = pivotDimensions
      .map((dimensionName) =>
        initialMembers.find((member) => member.name === dimensionName)
      )
      .filter(Boolean)
      .map((member, index) => ({
        ...member,
        originalIndex: index,
      }));

    setMembers(orderedMembers);
  }, [pivotConfig?.x, initialMembers]);

  const onReorder = useCallback((newOrder) => {
    const correctedOrder = [...newOrder].reverse();
    setMembers(correctedOrder);
  }, []);

  const onDragEnd = useCallback(
    (draggedMember) => {
      const oldIndex = pivotDimensionsRef.current.indexOf(draggedMember.name);

      // Create a new array based on the current members order
      const currentMemberNames = members.map((m) => m.name);

      // Create a full array including filtered items in their original positions
      const fullOrderArray = [...currentMemberNames];
      pivotDimensionsRef.current.forEach((name, index) => {
        if (!fullOrderArray.includes(name)) {
          fullOrderArray.splice(index, 0, name);
        }
      });

      // Find the new index in the full order array
      const newIndex = fullOrderArray.indexOf(draggedMember.name);

      if (oldIndex !== newIndex) {
        updatePivotConfig.moveItem({
          sourceIndex: oldIndex,
          destinationIndex: newIndex,
          sourceAxis: "x",
          destinationAxis: "x",
        });
      }
    },
    [members, updatePivotConfig]
  );

  const onAdd = useCallback(
    (newMember) => {
      setMembers((prevMembers) => [...prevMembers, newMember]);
      updateMethods.add(newMember);
    },
    [updateMethods]
  );

  return (
    <SectionRow style={{ flexFlow: "row nowrap" }}>
      <DraggableContainer
        axis="x"
        values={members}
        onReorder={onReorder}
        layoutScroll
      >
        {members.map((member) => (
          <DraggableItem
            key={member.name}
            value={member}
            member={member}
            availableMembers={availableMembers}
            intl={intl}
            updateMethods={updateMethods}
            onDragEnd={onDragEnd}
          />
        ))}
      </DraggableContainer>
      <MemberDropdown
        onClick={onAdd}
        availableMembers={availableMembers}
        type="dashed"
        icon={<PlusOutlined />}
        intl={intl}
      >
        {!members.length ? addMemberName : null}
      </MemberDropdown>
    </SectionRow>
  );
};

export default DraggedMemberGroup;
